import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  TextField, 
  MenuItem, 
  Button, 
  CircularProgress, 
  FormControlLabel, 
  Checkbox, 
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const DeptUnitPage = () => {
  useAuthCheck();
  const { deptUnitId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deptUnit, setDeptUnit] = useState({});
  const [departments, setDepartments] = useState([]);

  const initialValues = {
    deptUnitCode: deptUnit.deptUnitCode || '',
    description: deptUnit.description || '',
    departmentCode: deptUnit.departmentCode || '',
    sortOrder: deptUnit.sortOrder || 0,
    isActive: deptUnit.isActive !== undefined ? deptUnit.isActive : true,
  };

  useEffect(() => {
    const fetchDeptUnit = async () => {
      try {
        setLoading(true);
        const [duRes, deptRes] = await Promise.all([
          deptUnitId ? api.get(`/deptunits/${deptUnitId}`) : Promise.resolve({ data: {} }),
          api.get('/departments/active'),
        ]);
        if (deptUnitId) setDeptUnit(duRes.data);
        setDepartments(deptRes.data || []);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchDeptUnit();
  }, [deptUnitId]);

  const checkoutSchema = yup.object().shape({
    deptUnitCode: yup.string().required('Required'),
  });

  const handleFormSubmit = async (values) => {
    try {
      const method = deptUnitId ? 'PUT' : 'POST';
      const url = deptUnitId ? `/deptunits/update/${deptUnitId}` : '/deptunits/new';
      await api({ method, url, data: values });
      toast.success(deptUnitId ? 'Dept Unit updated successfully' : 'Dept Unit created successfully');
      navigate("/deptunits");
    } catch (error) {
      setError(error);
    }
  };

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Dept Unit"
          subtitle={loading ? 'Loading...' : deptUnitId ? 'Update' : 'Create'}
        />
        {!loading && (
          <StyledButton onClick={() => navigate("/deptunits")}>
            <ArrowBackIosNewIcon />
          </StyledButton>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}><Typography color="error">Error: {error?.message || ''}</Typography></FlexCenter>
      ) : (
        <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
          <Formik initialValues={initialValues} enableReinitialize validationSchema={checkoutSchema} onSubmit={handleFormSubmit}>
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <Box display="grid" gap={2} gridTemplateColumns="repeat(12, minmax(0, 1fr))" sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}>
                  <TextField
                    fullWidth 
                    required 
                    variant="filled" 
                    label="Dept Unit Code"
                    name="deptUnitCode" 
                    value={values.deptUnitCode}
                    onBlur={handleBlur} 
                    onChange={handleChange}
                    error={!!touched.deptUnitCode && !!errors.deptUnitCode}
                    helperText={touched.deptUnitCode && errors.deptUnitCode}
                    sx={{ gridColumn: 'span 12' }}
                  />
                  <TextField
                    fullWidth 
                    variant="filled" 
                    multiline 
                    rows={1} 
                    label="Description"
                    name="description" 
                    value={values.description}
                    onBlur={handleBlur} onChange={handleChange}
                    sx={{ gridColumn: 'span 12' }}
                  />
                  <TextField
                    fullWidth 
                    select 
                    variant="filled" 
                    label="Department"
                    name="departmentCode" 
                    value={values.departmentCode}
                    onBlur={handleBlur} 
                    onChange={handleChange}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ gridColumn: 'span 12' }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.departmentCode} value={d.departmentCode}>
                        {d.departmentCode} - {d.description}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth 
                    variant="filled" 
                    type="number" 
                    label="Sort Order"
                    name="sortOrder" 
                    value={values.sortOrder}
                    onBlur={handleBlur} 
                    onChange={handleChange}
                    slotProps={{ input: { sx: { "& input": { textAlign: "right" } } } }}
                    sx={{ gridColumn: 'span 12' }}
                  />
                  <FormControlLabel
                    sx={{ gridColumn: 'span 12' }}
                    control={<Checkbox name="isActive" checked={values.isActive} onChange={handleChange} color="primary" />}
                    label="Is Active"
                  />
                  <Button fullWidth type="submit" variant="contained" color="secondary" sx={{ gridColumn: 'span 12', height: 42 }}>
                    {deptUnitId ? "Update" : "Create"}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}
    </Box>
  );
};

export default DeptUnitPage;
